import {
	type DataNeededId,
	type DecisionId,
	Dtos,
	type ProConId,
	type SuretyAccountId,
} from "@inrev/common";
import { type MutationFailure, MutationFailureProvider } from "@portal/api/decisioning";
import {
	createContext,
	type ReactNode,
	type RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useToolsPanelWidth } from "../../../layout/ToolsPanelWidthContext";
import { type PanelToastOptions, usePanelToast } from "../../../patterns/panel-toast";
import { useExpandedIds } from "../useExpandedIds";
import { type RequestDraft } from "./data-needed/requestDraft";
import { type FactorEditDraft } from "./factors/factorEditDraft";

export type DecisioningTab = "overview" | "factors" | "data-needed" | "history";

export const LOG_DECISION_PARAM = "logDecision";

export const HIGHLIGHT_MS = 1800;

export type DecisioningSheet =
	| {
			kind: "factor";
			mode: "add";
			side: Dtos.Decisioning.ProConKind;
			importance?: Dtos.Decisioning.ProConImportance;
	  }
	| {
			kind: "factor";
			mode: "edit";
			proConId: ProConId;
			importance?: Dtos.Decisioning.ProConImportance;
			/** Restores what was typed, e.g. after the save failed. */
			draft?: FactorEditDraft;
	  }
	| {
			kind: "request";
			mode: "add";
			linkedProConId?: ProConId;
			/** Restores what was typed, e.g. after the create failed. */
			draft?: RequestDraft;
	  }
	| {
			kind: "request";
			mode: "edit";
			dataNeededId: DataNeededId;
			/** Restores what was typed, e.g. after the save failed. */
			draft?: RequestDraft;
	  }
	| { kind: "resolve"; dataNeededId: DataNeededId }
	| { kind: "connect"; proConId: ProConId }
	| {
			kind: "log";
			stage?: Dtos.Decisioning.DecisionStage;
			supersedesDecisionId?: DecisionId;
	  };

/** Scroll target for `jumpTo`. Items mark their root with `data-decisioning-item={id}`. */
export type JumpTarget = { tab: DecisioningTab; id: string };

type DecisioningPanelContextValue = {
	accountId: SuretyAccountId;
	panelWidth: number;
	tab: DecisioningTab;
	setTab: (tab: DecisioningTab) => void;
	isExpanded: (id: string) => boolean;
	setIsExpanded: (id: string, expanded: boolean) => void;
	highlightedId: string | null;
	jumpTo: (target: JumpTarget) => void;
	sheet: DecisioningSheet | null;
	openSheet: (sheet: DecisioningSheet) => void;
	closeSheet: () => void;
	toast: ReturnType<typeof usePanelToast>["toast"];
	showToast: (message: ReactNode, options?: PanelToastOptions) => void;
	/**
	 * Confirms a change once the server has saved it. A failed save is already reported by its
	 * mutation, so the confirmation is simply skipped and never shows ahead of the failure.
	 */
	toastOnSave: (saved: Promise<unknown>, message: ReactNode) => void;
	dismissToast: (id?: number) => void;
	scrollRef: RefObject<HTMLDivElement | null>;
};

const DecisioningPanelContext = createContext<DecisioningPanelContextValue | null>(null);

export const useDecisioningPanel = () => {
	const context = useContext(DecisioningPanelContext);
	if (!context) throw new Error("useDecisioningPanel must be used inside DecisioningPanelProvider");
	return context;
};

/** Styling for the 1.8s highlight `jumpTo` applies via `data-highlighted`. */
export const JUMP_HIGHLIGHT_CLASSES =
	"data-[highlighted]:bg-surface-accent data-[highlighted]:outline data-[highlighted]:outline-2 data-[highlighted]:outline-offset-1 data-[highlighted]:outline-bc-focus";

/** Props for an item `jumpTo` can scroll to and highlight. */
export const useJumpTargetProps = (id: string) => {
	const { highlightedId } = useDecisioningPanel();
	return {
		"data-decisioning-item": id,
		"data-highlighted": highlightedId === id || undefined,
	};
};

const isDecisionStage = (value: string | null): value is Dtos.Decisioning.DecisionStage =>
	Dtos.Decisioning.decisionStageSchema.safeParse(value).success;

/** Opens the Log decision sheet when `?logDecision=` is set (header button or the nudge), then clears it. */
const useLogDecisionParam = (openSheet: (sheet: DecisioningSheet) => void) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const value = searchParams.get(LOG_DECISION_PARAM);

	useEffect(() => {
		if (value === null) return;
		openSheet({ kind: "log", stage: isDecisionStage(value) ? value : undefined });
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete(LOG_DECISION_PARAM);
				return next;
			},
			{ replace: true },
		);
	}, [value, openSheet, setSearchParams]);
};

export const DecisioningPanelProvider = ({
	accountId,
	initialTab = "overview",
	initialSheet = null,
	initialExpandedIds,
	children,
}: {
	accountId: SuretyAccountId;
	initialTab?: DecisioningTab;
	initialSheet?: DecisioningSheet | null;
	/** Cards and snapshots that start expanded, e.g. for Storybook. */
	initialExpandedIds?: readonly string[];
	children: ReactNode;
}) => {
	const panelWidth = useToolsPanelWidth();
	const [tab, setTab] = useState<DecisioningTab>(initialTab);
	const { isExpanded, setIsExpanded } = useExpandedIds<string>(initialExpandedIds);
	const [highlightedId, setHighlightedId] = useState<string | null>(null);
	const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
	const [sheet, setSheet] = useState<DecisioningSheet | null>(initialSheet);
	const { toast, showToast, dismissToast } = usePanelToast();
	const scrollRef = useRef<HTMLDivElement | null>(null);

	const jumpTo = useCallback(({ tab: nextTab, id }: JumpTarget) => {
		setTab(nextTab);
		setPendingScrollId(id);
		setHighlightedId(id);
	}, []);

	useEffect(() => {
		if (pendingScrollId === null) return;
		const target = scrollRef.current?.querySelector<HTMLElement>(
			`[data-decisioning-item="${CSS.escape(pendingScrollId)}"]`,
		);
		if (!target) return;
		target.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
		setPendingScrollId(null);
	});

	useEffect(() => {
		if (highlightedId === null) return;
		const timer = setTimeout(() => setHighlightedId(null), HIGHLIGHT_MS);
		return () => clearTimeout(timer);
	}, [highlightedId]);

	const openSheet = useCallback((next: DecisioningSheet) => setSheet(next), []);
	const closeSheet = useCallback(() => setSheet(null), []);
	useLogDecisionParam(openSheet);

	const toastOnSave = useCallback(
		(saved: Promise<unknown>, message: ReactNode) => {
			saved.then(
				() => showToast(message),
				() => undefined,
			);
		},
		[showToast],
	);

	const showFailure = useCallback(
		({ message, action }: MutationFailure) => showToast(message, { tone: "danger", action }),
		[showToast],
	);

	const value = useMemo<DecisioningPanelContextValue>(
		() => ({
			accountId,
			panelWidth,
			tab,
			setTab,
			isExpanded,
			setIsExpanded,
			highlightedId,
			jumpTo,
			sheet,
			openSheet,
			closeSheet,
			toast,
			showToast,
			toastOnSave,
			dismissToast,
			scrollRef,
		}),
		[
			accountId,
			panelWidth,
			tab,
			isExpanded,
			setIsExpanded,
			highlightedId,
			jumpTo,
			sheet,
			openSheet,
			closeSheet,
			toast,
			showToast,
			toastOnSave,
			dismissToast,
		],
	);

	return (
		<DecisioningPanelContext.Provider value={value}>
			<MutationFailureProvider onFailure={showFailure}>{children}</MutationFailureProvider>
		</DecisioningPanelContext.Provider>
	);
};
